// Progressive enhancement of the real header /login link. OAuth launchers own
// authentication and browser-bound state; this module only presents the choice.
let opener = null;
let restoreFocus = true;
const dialog = () => document.getElementById('signin-options-dialog');
const visible = element => element?.isConnected && element.getClientRects().length > 0;

function safeReturnPath(value) {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/';
  try {
    const url = new URL(value, location.origin);
    return url.origin === location.origin ? url.pathname + url.search + url.hash : '/';
  } catch (_) { return '/'; }
}

function loginContext(trigger) {
  const href = trigger?.getAttribute('href') || '/login';
  const login = new URL(href, location.origin);
  const onAuthPage = ['/login', '/signup'].includes(location.pathname);
  const returnTo = safeReturnPath(onAuthPage
    ? new URLSearchParams(location.search).get('return_to') || login.searchParams.get('return_to') || '/'
    : location.pathname + location.search + location.hash);
  const email = new URL('/login', location.origin);
  email.searchParams.set('return_to', returnTo);
  return { returnTo, emailHref: email.pathname + email.search };
}

function restore() {
  document.documentElement.classList.remove('signin-options-open');
  const target = opener;
  opener = null;
  if (restoreFocus) {
    const fallback = Array.from(document.querySelectorAll('[data-header-auth-link], [data-site-header-toggle]')).find(visible);
    (visible(target) ? target : fallback)?.focus({ preventScroll: true });
  }
  restoreFocus = true;
}

function close(options = {}) {
  const element = dialog();
  if (!element?.open) return;
  restoreFocus = options.restoreFocus !== false;
  element.close();
}

function bind(element) {
  if (element.dataset.signinOptionsBound) return;
  element.dataset.signinOptionsBound = 'true';
  element.querySelector('[data-signin-options-close]')?.addEventListener('click', () => close());
  element.addEventListener('cancel', event => { event.preventDefault(); close(); });
  element.addEventListener('close', restore);
  element.addEventListener('click', event => {
    if (event.target !== element) return;
    const bounds = element.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
  });
}

function open(trigger = null) {
  const element = dialog();
  if (!element || typeof element.showModal !== 'function') return false;
  if (window.TeamBuildingAuthState?.authenticated || trigger?.dataset.headerAuthState === 'authenticated') return false;
  bind(element);
  const context = loginContext(trigger);
  element.querySelectorAll('[data-signin-provider]').forEach(link => {
    const provider = link.dataset.signinProvider;
    if (!['google', 'microsoft'].includes(provider)) return;
    const query = new URLSearchParams({ return_to: context.returnTo });
    link.href = `/auth/${provider}?${query}`;
  });
  element.querySelector('[data-signin-email]').href = context.emailHref;
  if (!element.open) {
    opener = trigger || document.activeElement;
    restoreFocus = true;
    // A mobile navigation disclosure should not remain open behind the dialog.
    document.querySelector('[data-site-header-toggle][aria-expanded="true"]')?.click();
    try { element.showModal(); } catch (_) { opener = null; return false; }
    document.documentElement.classList.add('signin-options-open');
  }
  element.scrollTop = 0;
  element.querySelector('#signin-options-title')?.focus({ preventScroll: true });
  return true;
}

function boot() {
  const element = dialog();
  if (element) bind(element);
}

window.TeamBuildingSigninOptions = { open, close };

document.addEventListener('click', event => {
  const trigger = event.target.closest('[data-header-auth-link]');
  if (!trigger || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (trigger.dataset.headerAuthState === 'authenticated' || window.TeamBuildingAuthState?.authenticated) return;
  const destination = new URL(trigger.href, location.origin);
  if (destination.origin !== location.origin || destination.pathname !== '/login') return;
  if (open(trigger)) event.preventDefault();
});

// A signed-in visitor can click before the cache-safe header check completes.
// Leave their now-hydrated Dashboard link available instead of showing sign-in.
document.addEventListener('teambuilding:auth-state', event => {
  if (event.detail?.authenticated === true) close();
});
['turbo:before-cache', 'turbolinks:before-cache'].forEach(name => {
  document.addEventListener(name, () => {
    close({ restoreFocus: false });
    document.documentElement.classList.remove('signin-options-open');
  });
});
['turbo:load', 'turbolinks:load'].forEach(name => document.addEventListener(name, boot));
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
